import { For, Show } from "solid-js";
import { Link, useLocation } from "@tanstack/solid-router";
import { cn } from "@listto/shared";
import {
  Bug,
  Calendar,
  CheckSquare,
  Inbox,
  LayoutDashboard,
  LogOut,
  Mail,
  Palette,
  Scan,
  Shield,
  ShieldAlert,
  Star,
  Sun,
  Type,
  User,
  Users,
} from "lucide-solid";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Logo } from "@/components/ui/logo";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { msg } from "@lingui/core/macro";

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

interface AppSidebarProps {
  stats: () =>
    | {
        all: number;
        myDay: number;
        important: number;
        planned: number;
        inbox: number;
      }
    | undefined;
  /** True while smart-view counts are still loading; renders count-badge skeletons. */
  statsLoading?: () => boolean;
  onCloseSidebar?: () => void;
}

export function AppSidebar(props: AppSidebarProps) {
  const session = useSession();
  const location = useLocation();
  const { t } = useI18n();
  const { promptSignOut } = useSignOut();

  // Guardamos el descriptor `msg` (NO el string traducido): `t()` se llama en el
  // render (`label={t(item.label)}`) para que sea REACTIVO al cambiar idioma.
  // Llamar `t()` aquí lo evaluaría una sola vez y el sidebar no cambiaría de idioma.
  const SMART_VIEWS = [
    { to: "/", label: msg`Dashboard`, icon: LayoutDashboard, key: "Dashboard" },
    { to: "/all", label: msg`Tasks`, icon: CheckSquare, key: "Tasks" },
    { to: "/my-day", label: msg`My Day`, icon: Sun, key: "My Day" },
    { to: "/important", label: msg`Important`, icon: Star, key: "Important" },
    { to: "/planned", label: msg`Planned`, icon: Calendar, key: "Planned" },
    { to: "/inbox", label: msg`Inbox`, icon: Inbox, key: "Inbox" },
  ];

  const ADMIN_ITEMS = [
    { to: "/organizations", label: msg`Organizations`, icon: Users },
    { to: "/invitations", label: msg`Invitations`, icon: Mail },
    { to: "/admin/storage", label: msg`Storage`, icon: Shield },
    { to: "/admin/audit", label: msg`Audit`, icon: Scan },
    { to: "/admin/themes", label: msg`Themes`, icon: Palette },
    { to: "/admin/theme-audit", label: msg`Theme Audit`, icon: ShieldAlert },
    { to: "/admin/fonts", label: msg`Fonts`, icon: Type },
  ];

  const getCount = (key: string): number | undefined => {
    const s = props.stats();
    if (!s) {
      return undefined;
    }
    switch (key) {
      case "Tasks": {
        return s.all;
      }
      case "My Day": {
        return s.myDay;
      }
      case "Important": {
        return s.important;
      }
      case "Planned": {
        return s.planned;
      }
      case "Inbox": {
        return s.inbox;
      }
      default: {
        return 0;
      }
    }
  };

  const handleSignOut = () => {
    props.onCloseSidebar?.();
    promptSignOut();
  };

  return (
    <div class="flex flex-col h-full pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      <div class="flex h-16 items-center px-6 shrink-0">
        <Logo size="sm" />
      </div>

      <Separator />

      <nav class="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 scrollbar-hide">
        <For each={SMART_VIEWS}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon === Star ? Star : item.icon} // Wait, Star is imported from lucide-solid
              count={getCount(item.key || "")}
              // The Dashboard (to "/") shows no count badge, so it gets no skeleton.
              countLoading={Boolean(props.statsLoading?.()) && item.to !== "/"}
              exact={item.to === "/"}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <Show when={session.data?.user.role === "admin"}>
          <div class="mt-4 px-3 mb-1">
            <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Administration`)}
            </h3>
          </div>
          <For each={ADMIN_ITEMS}>
            {(item) => (
              <SidebarNavItem
                to={item.to}
                label={t(item.label)}
                icon={item.icon}
                onClick={() => props.onCloseSidebar?.()}
              />
            )}
          </For>
        </Show>
      </nav>

      <Separator />

      {/* Bottom actions */}
      <div class="flex flex-col gap-0.5 p-2 shrink-0">
        <SidebarNavItem
          to="/profile"
          label={t(msg`Profile`)}
          icon={User}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/debug"
          label={t(msg`Debug`)}
          icon={Bug}
          onClick={() => props.onCloseSidebar?.()}
        />

        <Show
          when={session.isAuthenticated()}
          fallback={
            <Link
              to="/login"
              search={{ redirect: safeRedirectTarget(location().pathname) }}
              onClick={() => props.onCloseSidebar?.()}
              class={cn(
                "mt-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "bg-primary/10 text-primary hover:bg-primary/20",
              )}
            >
              <User class="h-4 w-4" />
              {t(msg`Sign In`)}
            </Link>
          }
        >
          <Button
            variant="ghost"
            class={`mt-1 w-full justify-start gap-2.5 px-3 py-2.5 ${inactiveClass} hover:text-destructive`}
            onClick={handleSignOut}
          >
            <LogOut class="h-4 w-4" />
            {t(msg`Sign Out`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
